import { User } from '@dc-shared-web/hooks/api/user'
import {
  DONE_SECTION_TITLE,
  OPTION_GROUP_MAP,
} from '@/app/crunches/[id]/InfoPanel/constants'
import {
  Answer,
  AnsweredQuestion,
  QuestionAnswer,
} from '@/dc-shared-web/src/hooks/api/cl-cs'
import {
  ChecklistPreview,
  CrunchData,
  QAnswer,
  Section,
  Survey,
  SurveyOption,
} from '@/dc-shared-web/src/hooks/api/document/types'
import { isUserSuperAdminAndInHQDoc } from '@/app/helpers/userHelper'

export const getOptionDisplayInfo = (
  optionGroup: keyof typeof OPTION_GROUP_MAP,
  option: string
) => {
  const data = OPTION_GROUP_MAP[optionGroup][option]
  return {
    textColor: data.textColor,
    borderColor: data.borderColor,
    hoverColor: data.hoverColor,
    lightColor: data.lightColor,
    bgColor: data.bgColor,
    icon: data.icon,
  }
}

export const isDescriptionEmpty = (description: string) =>
  description.trim().replace(/<p><br><\/p>/gi, '') === ''

export const formatRadioAnswerOption = (
  crunchData: CrunchData | undefined,
  previousAnswer: QuestionAnswer | undefined,
  newAnswer: Answer | undefined,
  skipAnswerAlteration?: boolean
): { id: number; value: Answer } | undefined => {
  if (!newAnswer) {
    return undefined
  }

  const answerId =
    crunchData?.surveyData.qOptionsFe[
      crunchData.surveyData.qOptionsFeTopLevel
    ].find((o) => o.optionChoiceName === newAnswer || o.label === newAnswer)
      ?.id || undefined

  if (!answerId) {
    return undefined
  }

  if (!skipAnswerAlteration && previousAnswer?.answer === newAnswer) {
    newAnswer = null
  }

  return {
    id: answerId,
    value: newAnswer,
  }
}

export const getAvailableSurveys = (
  appliedChecklists: ChecklistPreview[] | null | undefined,
  availableSurveys: SurveyOption[] | null | undefined
) => {
  const appliedSurveyIds =
    appliedChecklists?.map((c) => c.checklist_template_uuid) ?? []
  return (
    availableSurveys?.filter(
      (survey) => !appliedSurveyIds.includes(survey.ref_id)
    ) ?? []
  )
}

export const isComboCrunch = (crunchData: CrunchData | undefined) => {
  return crunchData?.meta.isBothChecklistAndPlaybook
}

export const isChecklist = (crunchData: CrunchData | undefined) => {
  return (
    (crunchData?.meta.isChecklist &&
      crunchData?.meta.checklist_type === 'check_list') ??
    false
  )
}

/**
 * Determine if a crunch is a cheatsheet (i.e. playbook)
 */
export const isCheatSheet = (crunchData: CrunchData | undefined) => {
  return (
    crunchData?.meta.isCheatSheet &&
    crunchData?.meta.checklist_type === 'cheat_sheet'
  )
}

const getQuestionOptionAnswer = (answer: QAnswer): string | null => {
  if (answer.options && answer.options[0]) {
    const answerOptions = answer.options
    if (answerOptions && answerOptions[0]) {
      const option = JSON.parse(answerOptions[0])
      const optVal = option.value
      return optVal
    }
  }
  return null
}

const getQuestionNote = (answer: QAnswer): string => {
  if (answer.notes_and_comments) {
    return answer.notes_and_comments || ''
  }
  return ''
}

const getSubQuestionAnswer = (id: string, answer: QAnswer) => ({
  id,
  value: answer.text
    ? answer.text
    : answer.options?.[0]
      ? JSON.parse(answer.options[0])?.value
      : '',
})

const getQuestionAnswer = (
  value: string | null,
  note: string,
  answer: QAnswer
): QuestionAnswer | null => {
  if (value || note) {
    return {
      answer: value,
      note: note,
      subQuestions: [],
      ...(answer.text && {
        instruction: answer.text,
        ai_generated: answer.ai_generated,
        last_updated_by: answer.last_updated_by,
      }),
    }
  }

  if (answer.text) {
    return {
      instruction: answer.text,
      ai_generated: answer.ai_generated,
      last_updated_by: answer.last_updated_by,
    }
  }

  return null
}

const parseQuestionId = (value: string): [string, string | undefined] => {
  if (!value.includes('/')) {
    return [value, undefined]
  }

  const split = value.split('/')
  return [split[1], split[0]]
}

export const getAnsweredQuestionsFromSurvey = (
  survey: Survey
): AnsweredQuestion =>
  Object.entries(survey.qAnswers).reduce((answeredQuestions, [key, answer]) => {
    const [questionId, subQuestionId] = parseQuestionId(key)

    if (subQuestionId) {
      if (!answeredQuestions[questionId]) {
        answeredQuestions[questionId] = {
          subQuestions: [],
        }
      }

      answeredQuestions[questionId].subQuestions!.push(
        getSubQuestionAnswer(subQuestionId, answer)
      )
    } else {
      const questionAnswer = getQuestionAnswer(
        getQuestionOptionAnswer(answer),
        getQuestionNote(answer),
        answer
      )

      if (questionAnswer) {
        if (answeredQuestions[questionId]) {
          questionAnswer.subQuestions =
            answeredQuestions[questionId].subQuestions
        }

        answeredQuestions[questionId] = questionAnswer
      }
    }

    return answeredQuestions
  }, {} as AnsweredQuestion)

export const updateActiveSections = (
  foundSection: Section,
  prev: string[] | undefined
) => {
  let prevSectionsState = prev ? [...prev] : undefined
  // If **DONE** exists, remove it
  if (prevSectionsState?.includes(DONE_SECTION_TITLE)) {
    prevSectionsState = prevSectionsState.filter(
      (section: string) => section !== DONE_SECTION_TITLE
    )
  }

  if (!prevSectionsState) return [foundSection?.title]

  if (foundSection && !prevSectionsState.includes(foundSection?.title)) {
    return [...prevSectionsState, foundSection?.title]
  }
  if (
    foundSection &&
    prevSectionsState.includes(foundSection?.title) &&
    prevSectionsState[prevSectionsState.length - 1] !== foundSection?.title
  ) {
    // if the found section already exists, move it to the end of the array
    return [
      ...prevSectionsState.filter(
        (section: string) => section !== foundSection?.title
      ),
      foundSection?.title,
    ]
  }
  return prev
}

export const getUserVCPermissions = ({
  user,
  hasVersionComparison,
  teamId,
}: {
  user: User
  hasVersionComparison: boolean
  teamId: string | undefined
}) => {
  if (!user) return false

  switch (true) {
    case !user._isUnaffiliatedUser && !user.is_super_admin:
      return true
    // UU can only see Version tab if there is an existing comparison
    case user._isUnaffiliatedUser && hasVersionComparison:
      return true
    // SA can see Version tab if there is an existing comparison
    case user.is_super_admin && hasVersionComparison:
      return true
    // Or SA can access Version tab if they are in HQ Doc team
    case user.is_super_admin &&
      isUserSuperAdminAndInHQDoc(user, teamId ? Number(teamId) : null):
      return true

    default:
      return false
  }
}
